import React, { useState, useEffect } from 'react';

import {
  Box,
  Paper,
  Divider,
  TextField,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  styled,
  Card,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import CommonBreadcumbs from '../common/CommonBreadcumbs';
import CommonSectionHeader from '../common/CommonSectionHeader';
import CommonRestricted from '../common/CommonRestricted';

import { initialValues, validationSchema } from '../../schema/business/businessSchema';

import { checkValueExists } from '../../utils/dataSchema';
import { useBusiness, businessTypes, targetTypes } from '../../hooks/api/useBusiness';
import { useRegions, regionTypes, targetTypes as regionTargetTypes } from '../../hooks/api/useRegions';
import { useSystemPrompt } from '../../hooks/useSystemPrompt';
import { useSystemRoutes, routeTypes } from '../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../hooks/usePermissions';
import { useTranslations } from '../../hooks/useTranslations';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default function NewBusiness({ routeType }) {
  const { t } = useTranslations({ ns: ['common', '@business'] });

  const { promptMessage } = useSystemPrompt();
  const { isLoading, create } = useBusiness({ businessType: businessTypes.business, targetType: targetTypes.business });
  const { isPermissionAllow } = usePermissions(
    routeType,
    scopeIds.business.id,
    scopeTypes[scopeIds.business.id].business.id
  );

  const { list: listRegions } = useRegions({});
  const [dataCountries, setDataCountries] = useState([]);
  const [dataStates, setDataStates] = useState([]);
  const [dataAreas, setDataAreas] = useState([]);

  const navigate = useNavigate();
  const { getRoute } = useSystemRoutes(routeType);
  const { getHtmlHead } = useSystemHead(routeType);
  const [dataRegions, setDataRegions] = useState([]);

  const onSubmit = async (currentValues) => {
    try {
      let data = {
        business: {
          company_name: currentValues?.companyName,
          id_number: currentValues?.idNumber,
          website: currentValues?.website,
          company_contact: currentValues?.companyContact,
          email: currentValues?.email,
          address_1: currentValues?.address1,
          address_2: currentValues?.address2,
          country_id: currentValues?.countryId,
          state_id: currentValues?.stateId,
          area_id: currentValues?.areaId,
          postcode: currentValues?.postcode,
          status: currentValues?.status ? 1 : 0,
        },
      };

      const response = await create({ data });

      promptMessage(true, `${response?.message}`);
      navigate(getRoute('settings/business'));
    } catch (error) {
      promptMessage(false, `Action Failed (${error?.message})`);
    }
  };

  const fetchDataCountry = async () => {
    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'country_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.country;

      const { data } = await listRegions(props);

      // console.log('re', data);

      setDataCountries(data?.countries);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataStates = async (countryId) => {
    setDataStates([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'state_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.state;
      props.countryId = countryId;

      const { data } = await listRegions(props);

      setDataStates(data?.states);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataAreas = async (stateId) => {
    setDataAreas([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'area_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.area;
      props.stateId = stateId;

      const { data } = await listRegions(props);

      setDataAreas(data?.areas);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit,
  });

  const { values, errors, touched, handleChange, setFieldValue } = formik;

  useEffect(() => {
    fetchDataCountry();
  }, []);

  useEffect(() => {
    if (formik?.values?.countryId) {
      if (dataStates.length) {
        if (dataStates[0].country_id !== formik.values.countryId) {
          fetchDataStates(formik.values.countryId);
        }
      } else {
        fetchDataStates(formik.values.countryId);
      }
    }

    if (formik?.values?.stateId) {
      if (dataAreas.length) {
        if (dataAreas[0].state_id !== formik.values.stateId) {
          fetchDataAreas(formik.values.stateId);
        }
      } else {
        fetchDataAreas(formik.values.stateId);
      }
    }
  }, [formik.values]);

  const checkPostcodeExists = () => {
    const checkExists = getPostcodesArray().find((i) => i === values.postcode);

    if (checkExists) return values.postcode;

    return '';
  };

  const getPostcodesArray = () => {
    let postcodeStringCheck = dataAreas.filter((i) => i.id === formik.values.areaId);

    if (postcodeStringCheck.length <= 0) return [];

    let postcodeString = postcodeStringCheck.length >= 0 ? postcodeStringCheck[0].postcode_string : '';

    let postcodeArray = postcodeString.includes(',') ? postcodeString.split(',') : [postcodeString];

    if (postcodeArray.length <= 0) return [];

    return postcodeArray;
  };

  const RenderBusinessInfo = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
          <CommonSectionHeader title={t('profile')} subTitle={t('fullDetailsOfBusinessProfile')} />
        </Grid>

        <Grid item xs={12} sm={12} md={8}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6} textAlign="left">
              <FormControlLabel
                control={
                  <Switch
                    sx={{
                      ml: 2,
                    }}
                    name="status"
                    checked={Boolean(values.status)}
                    onChange={(event, checked) => {
                      setFieldValue('status', checked);
                    }}
                  />
                }
                label={Boolean(values.status) ? t('enabled') : t('disabled')}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6} textAlign="left">
              <></>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'companyName'}
                label={t('companyName')}
                type="text"
                variant="outlined"
                value={values.companyName}
                onChange={handleChange}
                error={touched.companyName && Boolean(errors.companyName)}
                helperText={touched.companyName && errors.companyName}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'idNumber'}
                label={t('idNumber')}
                type="text"
                variant="outlined"
                value={values.idNumber}
                onChange={handleChange}
                error={touched.idNumber && Boolean(errors.idNumber)}
                helperText={touched.idNumber && errors.idNumber}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'website'}
                label={t('website')}
                type="text"
                variant="outlined"
                value={values.website}
                onChange={handleChange}
                error={touched.website && Boolean(errors.website)}
                helperText={touched.website && errors.website}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'email'}
                label={t('email')}
                type="text"
                variant="outlined"
                value={values.email}
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'companyContact'}
                label={t('contact')}
                type="text"
                variant="outlined"
                value={values.companyContact}
                onChange={handleChange}
                error={touched.companyContact && Boolean(errors.companyContact)}
                helperText={touched.companyContact && errors.companyContact}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderBusinessAddress = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={4} textAlign={'left'}>
          <CommonSectionHeader title={t('address')} subTitle={t('fullAddressDetailsOfBusinessInformation')} />
        </Grid>

        <Grid item xs={12} sm={12} md={8}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'address1'}
                label={t('address1')}
                type="text"
                variant="outlined"
                value={values.address1}
                onChange={handleChange}
                error={touched.address1 && Boolean(errors.address1)}
                helperText={touched.address1 && errors.address1}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'address2'}
                label={t('address2')}
                type="text"
                variant="outlined"
                value={values.address2}
                onChange={handleChange}
                error={touched.address2 && Boolean(errors.address2)}
                helperText={touched.address2 && errors.address2}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.countryId && Boolean(errors.countryId)}>
                <InputLabel id="country">{t('country')}</InputLabel>
                <Select
                  labelId="countryId"
                  label={t('country')}
                  name={'countryId'}
                  onChange={(event) => {
                    setFieldValue(`countryId`, event.target.value);
                    setFieldValue(`stateId`, '');
                    setFieldValue(`areaId`, '');
                    setDataStates([]);
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataCountries,
                    valueToCompare: values.countryId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataCountries.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.country_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.countryId && errors.countryId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.stateId && Boolean(errors.stateId)}>
                <InputLabel id="state">{t('state')}</InputLabel>
                <Select
                  labelId="stateId"
                  label={t('state')}
                  name={'stateId'}
                  onChange={(event) => {
                    setFieldValue(`stateId`, event.target.value);
                    setFieldValue(`areaId`, '');
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataStates,
                    valueToCompare: values.stateId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataStates.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.state_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.stateId && errors.stateId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.areaId && Boolean(errors.areaId)}>
                <InputLabel id="area">{t('area')}</InputLabel>
                <Select
                  labelId="areaId"
                  label={t('area')}
                  name={'areaId'}
                  onChange={handleChange}
                  value={checkValueExists({
                    dataSearch: dataAreas,
                    valueToCompare: values.areaId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataAreas.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.area_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.areaId && errors.areaId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.postcode && Boolean(errors.postcode)}>
                <InputLabel id="postcode">{t('postcode')}</InputLabel>
                <Select
                  labelId="postcode"
                  label={t('postcode')}
                  name={'postcode'}
                  onChange={handleChange}
                  value={checkPostcodeExists()}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {getPostcodesArray().length ? (
                    getPostcodesArray().map((i, index) => {
                      return (
                        <MenuItem key={index} value={i}>
                          {i}
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem value={''}>{null}</MenuItem>
                  )}
                </Select>
                <FormHelperText>{touched.postcode && errors.postcode}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderForm = () => {
    return (
      <form onSubmit={formik.handleSubmit}>
        <StyledCard>
          <Grid container spacing={1}>
            {RenderBusinessInfo()}

            <Grid item xs={12}>
              <Divider sx={{ mt: 1, mb: 1 }} />
            </Grid>

            {RenderBusinessAddress()}
          </Grid>
        </StyledCard>
        <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
          <Grid item md={12} textAlign={'right'}>
            <LoadingButton
              type="submit"
              // fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
              }}
              loading={isLoading}
            >
              {t('saveChanges')}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    );
  };

  return (
    <Box>
      {getHtmlHead({ routeType, title: headTitles.newBusiness })}

      <CommonBreadcumbs
        routeType={routeType}
        pageTitle={t('newBusiness')}
        breadcrumbs={[
          {
            displayText: t('businesses'),
            props: {
              href: getRoute(`settings/business`),
            },
          },
          {
            displayText: t('newBusiness'),
            props: {
              color: 'secondary.main',
            },
          },
        ]}
      />

      {isPermissionAllow({ functionType: functionTypes.pageNew.id }) ? RenderForm() : <CommonRestricted />}
    </Box>
  );
}
